import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import VideoModal from '../ui/VideoModal'
import { posterUrl } from '../ui/videoSource'
import ChallengeSelectVideo from './ChallengeSelectVideo'
import './challenges.css'

/*
 * THE VIDEOS ATTACHED TO ONE CONTENT RECORD.
 *
 * GET admin/challenges/challengevideos/:id                    the list
 * GET admin/challenges/challengevideosdelete/:id/:VideoID     detach one
 * Both untouched.
 *
 * The old grid mounted a full <VideoPlayer> in every row — a player per row,
 * each one loading its own stream, in a table that was never meant to hold
 * them. Now each row is a poster frame that opens the shared VideoModal, and
 * the modal walks the whole set, so the page loads one player at most and only
 * when somebody actually wants to watch something.
 *
 * The stream URLs come from videoSource, which strips a file extension before
 * building the HLS url and falls back through the naming conventions the video
 * library really holds — the hard-coded "{id}.smil" of the old code answered
 * 404 for part of the library.
 */

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
)

const IconPlus = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
)

const IconFilm = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zm6 3v7l6-3.5-6-3.5z" />
  </svg>
)

const IconX = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
)

/*
 * The picker dialog. Portalled to <body>, drawn from tokens, full-bleed under
 * 560px. Escape closes it — unless the shared VideoModal is open on top of it,
 * which is the one case where Escape belongs to the player.
 */
function PickerModal({ title, onClose, children }) {
  const dialogRef=useRef(null)

  useEffect(()=>{
    const onKey=(ev)=>{
      if (ev.key!=='Escape') return
      if (document.querySelector('.vm-backdrop')) return
      onClose()
    }
    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[onClose])

  useEffect(()=>{
    const node=dialogRef.current
    if (node) node.focus()
    const prev=document.body.style.overflow
    document.body.style.overflow='hidden'
    return ()=>{ document.body.style.overflow=prev }
  },[])

  return createPortal(
    <div className="chx-modal-back">
      <button type="button" className="chx-scrim" aria-label="Close" onClick={onClose}></button>

      <div className="chx-modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={dialogRef}>
        <div className="chx-modal-head">
          <h2 className="chx-modal-title">{title}</h2>
          <button type="button" className="chx-modal-close" onClick={onClose} aria-label="Close">
            <IconX/>
          </button>
        </div>

        <div className="chx-modal-body">{children}</div>
      </div>
    </div>,
    document.body
  )
}

export default function ChallengeVideos(props) {

    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [data,setData]=useState([])
    const [show, setShow] = useState(false)
    const [note,setNote]=useState(null)
    const [pending,setPending]=useState(null)
    const [clipIndex,setClipIndex]=useState(-1)

    const handleClose = useCallback(() => setShow(false), [])


    useEffect(()=>{
        setIsLoading(true)
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])


    const del=(VideoID)=>{
            setPending(null)
            setNote(null)
            axios.get(process.env.REACT_APP_API_SERVER+'admin/challenges/challengevideosdelete/'+props.id+'/'+VideoID,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{

                if (res.data.err==0){
                    loadData()
                }else{
                    setNote({tone:'crit',text:'Something went wrong, please try again'})
                }


              }).catch(err=>{
                  setIsLoading(false)
                  setNote({tone:'crit',text:'Something went wrong, please try again'})
              })

    }

    const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/challenges/challengevideos/'+props.id,{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{

          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            console.log(err)
        })
      }

    const rows=useMemo(()=>(Array.isArray(data) ? data : []),[data])
    const open=clipIndex>=0 && clipIndex<rows.length

    useEffect(()=>{
      if (clipIndex>=rows.length) setClipIndex(-1)
    },[rows.length,clipIndex])

    const closeClip=useCallback(()=>setClipIndex(-1),[])

    const cards=rows.map((row,index)=>{
      const poster=row.VideoID ? posterUrl(row.VideoID) : ''

      return (
        <li className="chx-vcard" key={(row.VideoID || 'video')+'-'+index}>
          <button
            type="button"
            className="chx-thumb"
            onClick={()=>setClipIndex(index)}
            title="Play video"
            aria-label={'Play video — '+row.VideoID}
          >
            {poster
              ? <img src={poster} alt="" loading="lazy" decoding="async"/>
              : <span className="chx-thumb-blank">No image</span>}
            <span className="chx-play" aria-hidden="true"><IconPlay/></span>
          </button>

          <div className="chx-vbody">
            <span className="chx-vid">{row.VideoID}</span>
          </div>

          <div className="chx-vfoot">
            <Btn onClick={()=>setClipIndex(index)}>
              <IconPlay/>
              Play
            </Btn>
            <Btn className="chx-btn-crit" onClick={()=>setPending(row.VideoID)}>Remove</Btn>
          </div>
        </li>
      )
    })

    return (
        <Panel
          title={(props.IsChallenge==1 ? 'Challenge' : 'Article')+' Videos'}
          subtitle={isLoading ? 'Loading…' : rows.length+' videos linked'}
          actions={<Btn variant="accent" onClick={()=>setShow(true)}><IconPlus/>Add Video</Btn>}
        >

            {note && (
              <div className="chx-note chx-note-crit" role="alert">
                <span className="chx-note-text">{note.text}</span>
              </div>
            )}

            {pending && (
              <div className="chx-confirm" role="alertdialog" aria-label="Confirm removal">
                <span className="chx-confirm-text">DELETE! Are you sure? — {pending}</span>
                <span className="chx-confirm-act">
                  <Btn onClick={()=>setPending(null)}>Cancel</Btn>
                  <Btn className="chx-btn-crit" onClick={()=>del(pending)}>Yes, delete</Btn>
                </span>
              </div>
            )}

            {isLoading && (
              <div className="chx-sk-grid">
                <div className="skeleton chx-sk-card"></div>
                <div className="skeleton chx-sk-card"></div>
                <div className="skeleton chx-sk-card"></div>
              </div>
            )}

            {!isLoading && failed && (
              <div className="chx-note chx-note-crit" role="alert">
                <span className="chx-note-text">The videos could not be loaded.</span>
                <Btn onClick={loadData}>Try again</Btn>
              </div>
            )}

            {!isLoading && !failed && rows.length===0 && (
              <div className="chx-empty">
                <span className="chx-empty-mark" aria-hidden="true"><IconFilm/></span>
                <h2 className="chx-empty-title">No videos</h2>
                <p className="chx-empty-text">
                  No video is linked to this entry. Press “Add Video”
                  to pick one from the library.
                </p>
              </div>
            )}

            {!isLoading && !failed && rows.length>0 && (
              <ul className="chx-grid">{cards}</ul>
            )}

            {show && (
              <PickerModal title="Select Video" onClose={handleClose}>
                <ChallengeSelectVideo ChallengeID={props.id} loadData={loadData} handleClose={handleClose}/>
              </PickerModal>
            )}

            {open && (
              <VideoModal
                items={rows.map(row=>({
                  fileId: row.VideoID,
                  name: row.VideoID,
                  subtitle: (props.IsChallenge==1 ? 'Challenge' : 'Article')+' Videos'
                }))}
                index={clipIndex}
                title="Videos"
                onIndex={setClipIndex}
                onClose={closeClip}
              />
            )}

        </Panel>
    )
}
